import { Button, CheckIcon, makeStyles } from '@freighthub/silhouette-core'
import { FC, useState } from 'react'
// eslint-disable-next-line import/no-cycle
import { useAnalytics } from 'service/useAnalytics'
import { useRootStore } from 'service/useRootStore'
import { I18N } from 'utils/localisation'

import { CreateBookingTemplate } from '../BookingTemplate'
import { BookingTemplateNameDialog } from './BookingTemplateNameDialog'

const useStyles = makeStyles(() => ({
  root: {
    flex: '1 1 0%',
    marginRight: 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  saved: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}))

export type GetBookingTemplate = (name: string) => CreateBookingTemplate

type SaveAsBookingTemplateButtonProps = {
  getBookingTemplate: GetBookingTemplate
}

export const SaveAsBookingTemplateButton: FC<SaveAsBookingTemplateButtonProps> = ({ getBookingTemplate }) => {
  const classes = useStyles()
  const { bookingTemplateService } = useRootStore()
  const analyticsService = useAnalytics()

  const [templateName, setTemplateName] = useState('')
  const [isSaved, setIsSaved] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const openDialog = () => setIsDialogOpen(true)
  const closeDialog = () => setIsDialogOpen(false)

  const isValid = templateName.length > 0

  const saveTemplate = async () => {
    setIsLoading(true)
    const bookingTemplate = getBookingTemplate(templateName)

    try {
      await bookingTemplateService.createBookingTemplate(bookingTemplate)
      setIsSaved(true)
      closeDialog()
      analyticsService.sendEvent('booking-template-save-as-template')
    } catch (e) {
      if (typeof e === 'string') {
        setErrorMessage(e)
      } else if (e && e instanceof Error && typeof e.message === 'string') {
        setErrorMessage(e.message)
      } else {
        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        setErrorMessage(e.toString())
      }
    } finally {
      setIsLoading(false)
    }
  }

  const hasBillingAddress = !!getBookingTemplate(templateName).legalEntity.billingAddress

  return (
    <span className={classes.root}>
      {isSaved ? (
        <span className={classes.saved}>
          <CheckIcon />
          <I18N term="booking_templates-template_saved" />
          <span>&ensp;&quot;</span>
          <span className={classes.name}>{templateName}</span>
          <span>&quot;</span>
        </span>
      ) : (
        <Button onClick={openDialog} disabled={isLoading || !hasBillingAddress}>
          <I18N term="booking_templates-save_as_template_button" />
        </Button>
      )}

      {isDialogOpen && (
        <BookingTemplateNameDialog
          closeDialog={closeDialog}
          isValid={isValid}
          templateName={templateName}
          setTemplateName={setTemplateName}
          errorMessage={errorMessage}
          isLoading={isLoading}
          save={saveTemplate}
        />
      )}
    </span>
  )
}
